import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import type { VinculoEstipulanteItem } from '../../types/estipulanteVinculoType';
import type {
  GenerateAttendanceProtocolResponse,
  GenerateAttendanceProtocolReturn,
} from '../../types/vida/protocol/protocolType';
import {
  clearPersistedVidaProtocol,
  normalizeGerarProtocoloRetorno,
  persistVidaProtocolCredentials,
  readRefreshPersistedProtocolRetornoForRamo4Vinculo,
} from '../../utils/vidaProtocolStorage';

const initialState = {
  attendanceProtocol: null as GenerateAttendanceProtocolReturn | null,
  protocolMessage: null as string | null,
  loading: false,
  error: null as string | null,
};

export interface VidaProtocolAtendimentoState {
  attendanceProtocol: GenerateAttendanceProtocolReturn | null;
  protocolMessage: string | null;
  loading: boolean;
  error: string | null;
  clear: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  applySuccessfulResponse: (response: GenerateAttendanceProtocolResponse) => void;
  /** Falha do GET gerar-protocolo: zera store e grava sentinhas vazias no LS via {@link persistVidaProtocolCredentials}. */
  applyRemoteProtocolFetchFailure: (message: string) => void;
  /** Reload / CompanyPage: if the vinculo is ramo Vida (4), hydrate the store from `localStorage`. */
  hydrateFromPersistedStorageIfRamo4Vinculo: (item: VinculoEstipulanteItem | null) => boolean;
}

export const useVidaProtocolAtendimentoStore = create<VidaProtocolAtendimentoState>()(
  subscribeWithSelector((set) => ({
    ...initialState,
    clear: () => {
      clearPersistedVidaProtocol();
      set({ ...initialState });
    },
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    applySuccessfulResponse: (response) => {
      const normalized = normalizeGerarProtocoloRetorno(response?.retorno ?? null);
      if (!normalized) {
        persistVidaProtocolCredentials(null, null);
        set({
          attendanceProtocol: null,
          protocolMessage: response?.mensagem ?? null,
          error: null,
        });
        return;
      }
      persistVidaProtocolCredentials(normalized.idProtocolo, normalized.chaveProtocolo);
      set({
        attendanceProtocol: normalized,
        protocolMessage: response?.mensagem ?? null,
        error: null,
      });
    },
    applyRemoteProtocolFetchFailure: (message) => {
      persistVidaProtocolCredentials(null, null);
      set({
        attendanceProtocol: null,
        protocolMessage: null,
        error: message,
      });
    },
    hydrateFromPersistedStorageIfRamo4Vinculo: (item) => {
      const retorno: GenerateAttendanceProtocolReturn | null =
        readRefreshPersistedProtocolRetornoForRamo4Vinculo(item);
      if (!retorno) {
        return false;
      }
      set({
        attendanceProtocol: retorno,
        protocolMessage: null,
        error: null,
      });
      return true;
    },
  })),
);
